import { useCallback, useEffect, useMemo, useState } from 'react';
import { compareDesc } from 'date-fns';
import makeRequest from '@llt-frontend/make-request';
import { useFetch } from '@llt-frontend/react-fetch';

const getPackagesURL = (clientId) =>
  `${process.env.REACT_APP_API_HOST}/v4/clients/${clientId}/available_packages`;

const getPurchaseURL = () =>
  `https://www.${process.env.REACT_APP_DOMAIN_NAME}/account/client/api/v2/client/hour-balances.json`;

export const useGetMostRecentPackagePurchase = () => {
  const { isLoading, error, data } = useFetch(getPurchaseURL());

  const mostRecentPackagePurchase = useMemo(
    () =>
      data?.data
        // filter out non-package rate type
        ?.filter(({ rate_type_id }) => rate_type_id !== 27)
        // group all recent transactions
        .reduce((acc, current) => [...acc, ...current.recent_transactions], [])
        // only look at positive durations ???
        .filter(({ duration }) => duration > 0)
        // sort to find the most recent
        .sort((a, b) => compareDesc(new Date(a.occurred_at), new Date(b.occurred_at)))?.[0],
    [data]
  );

  return { isLoading, error, mostRecentPackagePurchase };
};

export const useGetAvailablePackages = (clientId) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [availablePackages, setAvailablePackages] = useState<any>();

  const getPackages = useCallback(async (id) => {
    setIsLoading(true);
    setError(false);
    let packageResponse;

    try {
      packageResponse = await makeRequest(getPackagesURL(id));
    } catch (err) {
      console.error(err);
      setError(true);
      setIsLoading(false);
      return;
    }

    if (!packageResponse.packages) {
      setError(true);
      setIsLoading(false);
      return;
    }

    const relevant = packageResponse.packages
      // group by rate type
      .reduce((acc, current) => {
        if (!acc[current.package_type_id]) {
          acc[current.package_type_id] = [];
        }
        acc[current.package_type_id].push(current);
        return acc;
      }, {});

    setAvailablePackages(relevant);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (clientId && !isLoading && !error && !availablePackages) {
      getPackages(clientId);
    }
  }, [clientId, getPackages, isLoading, error, availablePackages]);

  return { isLoading, error, availablePackages };
};

export const useGetRepurchaseOptions = (clientId) => {
  // get the packages the user *can currently* purchase and the ones they *have* purchased
  // find overlap between them and offer repurchase/upsell options
  const { availablePackages, ...availableRes } = useGetAvailablePackages(clientId);
  const { mostRecentPackagePurchase, ...recentRes } = useGetMostRecentPackagePurchase();

  const { mostRecentPackage, upsellPackage } = useMemo(() => {
    const packageOptions = availablePackages?.[mostRecentPackagePurchase?.rate_type_id];
    if (!packageOptions) {
      return {};
    }

    const mostRecentPackage = packageOptions.filter(
      ({ duration_seconds }) => duration_seconds === mostRecentPackagePurchase.duration
    )?.[0];

    const upsellPackage = packageOptions
      .filter(({ duration_seconds }) => duration_seconds > mostRecentPackagePurchase.duration)
      .sort((a, b) => a.duration_seconds - b.duration_seconds)?.[0];

    return { mostRecentPackage, upsellPackage };
  }, [availablePackages, mostRecentPackagePurchase]);

  return {
    mostRecentPackage,
    upsellPackage,
    isLoading: availableRes.isLoading || recentRes.isLoading,
    error: availableRes.error || recentRes.error,
  };
};
